import { HoldEvent } from '@wui/jssip/lib/RTCSession'
import { defineStore } from 'pinia'
import { ref } from 'vue'

type State = Map<string, { local: boolean, remote: boolean }>

export const useHoldStore = defineStore('hold', () => {
  const holdStates = ref<State>(new Map())

  const setState = (sessionId: string, originator: HoldEvent['originator'], value: boolean) => {
    const prev = holdStates.value.get(sessionId) ?? { local: false, remote: false }
    const next = {
      local: originator === 'local' ? value : prev.local,
      remote: originator === 'remote' ? value : prev.remote,
    }
    // Replace Map so Vue/Pinia subscribers (CallPrivate hold UI) re-render.
    holdStates.value = new Map(holdStates.value).set(sessionId, next)
  }

  const removeState = (sessionId: string) => {
    const next = new Map(holdStates.value)
    next.delete(sessionId)
    holdStates.value = next
  }

  const getIsRemoteHold = (sessionId: string) => !!holdStates.value.get(sessionId)?.remote

  const getIsLocalHold = (sessionId: string) => holdStates.value.get(sessionId)?.local

  /** Private call: local or remote hold (reactive via holdStates Map replace). */
  const getIsOnHold = (sessionId: string) => {
    const state = holdStates.value.get(sessionId)
    return Boolean(state?.local || state?.remote)
  }

  return {
    setState,
    removeState,
    getIsRemoteHold,
    getIsLocalHold,
    getIsOnHold,
  }
})

type ResolveIsCallOnHoldParams = {
  sessionId: string
  isGroup: boolean
  facadeIsOnHold: boolean
  /** From JsSIP `session.isOnHold()` for conferences; ignored for private. */
  sessionHoldState?: { local?: boolean, remote?: boolean } | false | null
}

/** Unified hold check for UI controls / hotkeys (WUI-5282). */
export const resolveIsCallOnHold = ({
  sessionId,
  isGroup,
  facadeIsOnHold,
  sessionHoldState,
}: ResolveIsCallOnHoldParams) => {
  if (facadeIsOnHold) {
    return true
  }

  if (!isGroup) {
    return useHoldStore().getIsOnHold(sessionId)
  }

  if (!sessionHoldState) {
    return false
  }

  return Boolean(sessionHoldState.local || sessionHoldState.remote)
}
